/**
 * USER SETTINGS PAGE (TS-33) — Hub impostazioni accessibile dal menu utente in
 * alto a destra. Layout a 2 colonne: a sinistra l'elenco delle applicazioni di
 * configurazione, a destra l'applicazione aperta.
 *
 * La sezione attiva è riflessa nell'URL: /settings/user (account, default),
 * /settings/user/audit. Il click sul nav naviga; lo stato deriva dal path param,
 * quindi deep-link e back/forward del browser funzionano.
 *
 * Qui stanno solo le impostazioni di CHI È LOGGATO. "Utenti e team" è passato
 * alle impostazioni dell'applicazione (/settings/teams, voce "Impostazioni" del
 * menu laterale): configura l'installazione, non l'utente corrente.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AccountSettings } from '../../components/settings/AccountSettings';
import { AuditLogSettings } from '../../components/settings/AuditLogSettings';

type SettingsApp = 'account' | 'audit';

// Mappa app -> URL. account = base (nessun segmento); audit = sotto-path.
const PATH_BY_APP: Record<SettingsApp, string> = {
  account: '/settings/user',
  audit: '/settings/user/audit',
};

/** Risolve la sezione attiva dal path param; default account. */
function appFromSection(section: string | undefined): SettingsApp {
  if (section === 'audit') return 'audit';
  return 'account';
}

export function UserSettingsPage() {
  const { t } = useTranslation();
  const { section } = useParams();
  const navigate = useNavigate();
  const app = appFromSection(section);

  const apps: { key: SettingsApp; label: string; enabled: boolean }[] = [
    { key: 'account', label: t('userSettings.apps.account'), enabled: true },
    { key: 'audit', label: t('userSettings.apps.audit'), enabled: true },
  ];

  return (
    <div className="settings-layout">
      <nav className="settings-nav">
        <h2 className="settings-nav__title">{t('userSettings.title')}</h2>
        <ul className="settings-nav__list">
          {apps.map((a) => (
            <li key={a.key}>
              <button
                type="button"
                className={`settings-nav__item${app === a.key ? ' settings-nav__item--active' : ''}`}
                disabled={!a.enabled}
                onClick={() => a.enabled && navigate(PATH_BY_APP[a.key])}
              >
                <span>{a.label}</span>
                {!a.enabled && (
                  <span className="settings-nav__badge">{t('actions.comingSoon')}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <section className="settings-content">
        {app === 'account' && <AccountSettings />}
        {app === 'audit' && <AuditLogSettings />}
      </section>
    </div>
  );
}
